<template>
  <div
    v-if="collectionStore.extraOptionsModalOpen"
    id="extra-options-modal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 sm:py-8"
  >
    <div
      v-on-click-outside="closeModal"
      class="container relative flex h-full max-h-dvh max-w-[75rem] select-none flex-col items-center bg-white px-0 shadow-md md:h-auto md:rounded-md"
    >
      <!-- Header -->
      <div class="flex w-full items-center justify-between px-6 py-2 shadow-sm">
        <h3
          v-t="'pages.navigation.header.extra_options'"
          class="text-2xl font-bold"
        />

        <div
          class="relative flex h-9 w-8 cursor-pointer items-center justify-center rounded-full bg-white"
          @click="closeModal"
        >
          <XMarkIcon
            class="h-6 w-6 text-gray-400 transition-colors hover:text-black"
          />
        </div>
      </div>

      <!-- Body -->
      <div
        :class="{
          'flex h-full items-center justify-center':
            extraOptionsStore.productOptionsCategories.length === 0 ||
            extraOptions.request.isLoading,
        }"
        class="scrollbar-custom w-full overflow-y-auto overflow-x-hidden rounded-b-md pb-20"
      >
        <div class="px-2">
          <div
            v-if="
              extraOptionsStore.productOptionsCategories.length === 0 ||
              extraOptions.request.isLoading
            "
            class="flex h-full w-full items-center justify-center py-8"
          >
            <SpinnerIcon size="h-12 w-12" />
          </div>

          <template
            v-else-if="extraOptionsStore.productOptionsCategories.length"
          >
            <ProductOptionCategory
              v-for="(
                productOptionCategory, index
              ) in extraOptionsStore.productOptionsCategories"
              :key="`product_option_category_${index}`"
              :category="productOptionCategory"
            />
          </template>
        </div>

        <!-- Show overview on desktop -->
        <CollectionExtraOptionsOverview
          class="hidden w-full rounded-b-md md:block"
        />

        <!-- Sticky buttons for mobile -->
        <div
          class="absolute bottom-0 left-0 flex w-full justify-between gap-x-4 rounded-b-md bg-white md:hidden"
        >
          <div class="container my-3 flex h-14 w-full items-center gap-x-4">
            <BackButton class="w-1/5" @click="closeModal" />

            <AddToCartButton />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BackButton from '@/components/Buttons/BackButton.vue';
import SpinnerIcon from '@/components/Icons/SpinnerIcon.vue';
import ProductOptionCategory from '@/components/ProductOptions/ProductOptionCategory.vue';
import { useExtraOptions } from '@/composables/UseExtraOptions';
import { useCollectionStore } from '@/stores/Collection/CollectionStore';
import { useExtraOptionsStore } from '@/stores/ExtraOptionsStore';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { vOnClickOutside } from '@vueuse/components';
import { onMounted, onUnmounted } from 'vue';
import AddToCartButton from './AddToCartButton.vue';
import CollectionExtraOptionsOverview from './CollectionExtraOptionsOverview.vue';

const collectionStore = useCollectionStore();
const extraOptions = useExtraOptions();
const extraOptionsStore = useExtraOptionsStore();

function closeModal(): void {
  collectionStore.extraOptionsModalOpen = false;
}

function escapeHandler(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    closeModal();
  }
}

onMounted(() => {
  window.addEventListener('keydown', escapeHandler);
});

onUnmounted(() => {
  window.removeEventListener('keydown', escapeHandler);
});
</script>

<style>
.scrollbar-custom::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-bottom-right-radius: 0.375rem;
}

.scrollbar-custom::-webkit-scrollbar-thumb {
  background: #c1c1c1;
}

.scrollbar-custom::-webkit-scrollbar {
  width: 10px;
}
</style>
